import React, { useEffect } from 'react';
import { SecondaryCollapsibleSection } from "../../../../../../../elements/collapsableSection/secondary/SecondaryCollapsibleSection";
import { CustomSwitchFieldWithTitle } from "../../../../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import { useAppDispatch, useAppSelector } from "../../../../../../../../app/hooks";
import {
    selectModifierRules,
    selectFieldOptions,
    selectFieldOptionsStatus,
    selectModifierRulesIsActive,
    fetchFieldOptions,
    updateModifierRulesBasic,
    addRuleSection
} from "../../../toolSettingsSlice";
import { RequestStatus } from "../../../../../../../../api/apiConfiguration";
import { UpdateBotToolSettingsRequest } from "../../../../../../../../api/generated";
import { ValidationResult } from "../../../../../../../elements/validator/component/ValidationComponent";
import { ModifierRulesValidator } from "../../../../../../../elements/validator/Validator";
import { DependantValidationComponent } from "../../../../../../../elements/validator/dependant/DependantValidationComponent";
import { RuleSection } from "./RuleSection";
import styles from "./ModifierRules.module.css";
import {useRequestErrorToast} from "../../../../../../../../hooks/useRequestErrorToast";

interface ModifierRulesProps {
    disabled?: boolean;
    updateValidationStatus: (status: ValidationResult<UpdateBotToolSettingsRequest>) => void;
    getMessageWrapper: (f: string) => string;
}

export const ModifierRules: React.FC<ModifierRulesProps> = ({ disabled, updateValidationStatus, getMessageWrapper }) => {
    const dispatch = useAppDispatch();
    const modifierRules = useAppSelector(selectModifierRules);
    const fieldOptions = useAppSelector(selectFieldOptions);
    const fieldOptionsStatus = useAppSelector(selectFieldOptionsStatus);

    useEffect(() => {
        if (fieldOptionsStatus === RequestStatus.NONE) {
            dispatch(fetchFieldOptions());
        }
    }, [dispatch, fieldOptionsStatus]);

    useRequestErrorToast(fieldOptionsStatus, "Failed to load valuation rule field options");

    const handleToggleActive = (isActive: boolean) => {
        if (modifierRules) {
            dispatch(updateModifierRulesBasic({ isActive }));
        }
    };

    const handleAddRuleSection = () => {
        dispatch(addRuleSection());
    };

    const sections = modifierRules?.ruleSections || [];

    return (
        <SecondaryCollapsibleSection title="Modifier Rules" disabled={disabled}>
            <div className={styles.wrapper}>
                <CustomSwitchFieldWithTitle
                    textOnTrue={"Active"}
                    textOnFalse={"Inactive"}
                    title={"Enable Modifier Rules"}
                    description={"Modify the vehicle price or exclude from the price calculation based on specific conditions"}
                    valueSelector={(state) => state.toolSettings.updateRequest.modifierRules?.isActive || false}
                    toggleValue={handleToggleActive}
                />

                {modifierRules?.isActive && (
                    <DependantValidationComponent<any, UpdateBotToolSettingsRequest>
                        selector={selectModifierRules}
                        anchorValueSelector={selectModifierRulesIsActive}
                        fieldName={'modifierRules' as keyof UpdateBotToolSettingsRequest}
                        validator={ModifierRulesValidator}
                        updateValidationStatus={updateValidationStatus}
                        onChange={() => {}}
                        label="Modifier Rules"
                        required={true}
                        disabled={disabled || !modifierRules?.isActive}
                        child={(label, value, onChange, required) => (
                            <div className={styles.sections}>
                                {sections.map((section, sectionIndex) => (
                                    <RuleSection
                                        key={sectionIndex}
                                        section={section}
                                        sectionIndex={sectionIndex}
                                        fieldOptions={fieldOptions}
                                        disabled={disabled}
                                        updateValidationStatus={updateValidationStatus}
                                        getMessageWrapper={getMessageWrapper}
                                    />
                                ))}

                                <button
                                    className={styles.addSectionButton}
                                    onClick={handleAddRuleSection}
                                    disabled={disabled || !modifierRules?.isActive}
                                >
                                    + Add Rule Section
                                </button>
                            </div>
                        )}
                    />
                )}
            </div>
        </SecondaryCollapsibleSection>
    );
};

